import React, { useRef } from 'react';
import { useInView } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import PhoneSwap from '../PhoneSwap';
import GhostNumeral from '../GhostNumeral';
import Chips from '../Chips';

/**
 * 06 · COMPETE — adherence is the unsolved problem in preventive health.
 * The one section that runs hot: amber pressure, a live clash, a lead at risk.
 */
const StonRoom = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-120px' });

  const rise = (delay) => ({
    initial: { opacity: 0, y: 26 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section
      id="ston-room"
      ref={ref}
      className="relative py-24 md:py-32 bg-ston-bg overflow-hidden"
      data-testid="ston-room-section"
    >
      {/* Amber pressure ambience */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 right-0 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(245,158,11,0.06), rgba(245,158,11,0.030) 55%, transparent 78%)' }} />
        <div className="absolute bottom-0 -left-32 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(147,51,234,0.08), rgba(147,51,234,0.040) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">

          {/* Devices — crew pressure front, leaderboard behind */}
          <motion.div
            className="relative flex justify-center lg:justify-start order-2 lg:order-1"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative" style={{ perspective: 1500 }}>
              <GhostNumeral
                className="absolute -top-16 -right-8 md:-right-20"
                stroke="rgba(251,191,36,0.16)"
                size="clamp(120px, 13vw, 200px)"
              >
                705
              </GhostNumeral>

              <PhoneSwap
                side="left"
                width={296}
                front={{
                  src: '/screens/room-crew-detail.webp',
                  alt: 'Crew pulse — lead at risk, Tushar only 4 STON back, Streak Wars live',
                  glowColor: 'rgba(251,191,36,0.2)',
                }}
                back={{
                  src: '/screens/room-leaderboard.webp',
                  alt: 'Crew leaderboard — Prajyot leading with 705 STON, Tushar 4 behind',
                }}
              />
            </div>
          </motion.div>

          {/* Copy */}
          <div className="order-1 lg:order-2">
            <motion.span className="section-label inline-block" {...rise(0)}>
              06 · COMPETE
            </motion.span>
            <motion.h2
              className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
              style={{ fontSize: 'clamp(30px, 4vw, 52px)', letterSpacing: '-0.025em' }}
              {...rise(0.1)}
            >
              <span className="block text-ston-text">Health habits</span>
              <span
                className="block"
                style={{
                  background: 'linear-gradient(100deg, #fde68a 0%, #fbbf24 50%, #f59e0b 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                don't survive alone.
              </span>
            </motion.h2>
            <motion.p
              className="mt-6 text-[17px] text-ston-text-secondary leading-relaxed max-w-[52ch]"
              {...rise(0.2)}
            >
              Form a crew. Every session, streak and PR feeds a live STON score.
              Weekly clashes with titles on the line. Your crew sees the days you
              skip — which turns out to be the only thing that reliably works.
            </motion.p>
            <Chips items={['Crews', 'STON score /1000', 'Weekly clashes', '1v1 duels']} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default StonRoom;
